import { AdvancedMarker, useAdvancedMarkerRef, useMap } from "@vis.gl/react-google-maps";
import { AdvancedMarkerProps } from "@vis.gl/react-google-maps/src/components/advanced-marker";
import React, { ReactNode, useCallback, useEffect, useMemo, useState, useRef, useSyncExternalStore } from 'react';
import type { VesselUpdates } from "./vesselUpdates";
import DurationReel from "../../components/DurationReel";
import { diseaseType } from "../../util/varibles/constants";
import { checkZero, getHourMinute, getStatusOfOp, percentBrightness } from "../../util/varibles/global";
import { getWorldMap, setWorldMap, useWorldMap } from "./";
import { getMetersPerPx, MAP_ELEMENT, markerProperty } from "./constants";
import Dialog from "./Dialog";
import { useMarkerPopup } from "./MarkerPopup";
import { colors } from "./Polyline";
import styles from "./style.module.scss";

export const vesselColors = [
    { strokeColor: '#FF8D68', fillColor: '#FFD24C' }, //Process vessel
    { strokeColor: '#1B6399', fillColor: '#5EB0ED' }, //Wellboat
    { strokeColor: '#EE2A2A', fillColor: '#FF988F' }, //Treatment
]
export const createIconVessel = ({ ...args }) => {
    const { width, length, metersPerPx, vesselType } = args;
    const minus = Math.round(length / 10 / metersPerPx * 10) / 10;
    let x = Math.round(width / 2 / metersPerPx * 10) / 10;
    let y = Math.round(length / 2 / metersPerPx * 10) / 10;
    let path;
    if (x < 3 || y < 3) {
        path = 'M -4.6 -6 L 0 -12 L 4.6 -6 L 4.6 9 L 0 5 L -4.6 9 Z';
        x = 9.2;
        y = 21;
    } else
        path = `M -${x} -${y - x}
            L 0 -${y + minus} 
            L ${x} -${y - x} 
            L ${x} ${y - minus} 
            L -${x} ${y - minus} 
            Z`
    return {
        path,
        strokeWeight: 4,
        x,
        y: y + minus,
        height: y * 2,
        width: x * 2,
        ...vesselColors[vesselType - 1]
    }
};

const Name: React.FC<{ children?: ReactNode }> = ({ children }) => {
    const [hideLabel] = useWorldMap(store => store.hideLabel);

    return useMemo(() => {
        if (hideLabel)
            return null

        return <span style={{
            color: '#FFF',
            position: 'absolute',
            top: '18px',
            left: '50%',
            transform: 'translateX(-50%)',
            whiteSpace: 'nowrap'
        }}>
            {children}
        </span>
    }, [hideLabel, children]);
};

interface ICommonMarker {
    show?: boolean
    showPopup?: boolean

}

export interface IPropsMarker extends ICommonMarker {
    data: {
        name?: string
        position?: {
            latitude: number
            longitude: number
        }
        [id: string]: any
    }

    onClick?(e?: google.maps.MapMouseEvent): void

    onContextmenu?(e?: PointerEvent): void

    onUpdate?(event: google.maps.MapMouseEvent): void
}


interface IMarker extends ICommonMarker {
    markerType: MAP_ELEMENT
    dialog?: () => ReactNode
    children?: ReactNode
    markerProps?: AdvancedMarkerProps
    updates?: VesselUpdates
    onContextmenu?(e?: MouseEvent): void
}

const Marker: React.FC<IMarker> = ({
    show = true,
    showPopup,
    markerType,
    dialog,
    children,
    markerProps,
    updates,
    onContextmenu
}) => {
    const [markerRef, marker] = useAdvancedMarkerRef();
    const map = useMap();
    const [visible, setVisible] = useState(false)
    const popup = useMarkerPopup();
    const [popupKey] = useState(() => ({}));
    const set = setWorldMap();
    const get = getWorldMap();

    useEffect(() => {
        if (marker) {
            marker.map = show ? map : null;
        }
    }, [marker, map, show])

    useEffect(() => {
        if (!marker || !updates) return;
        const applyPosition = () => {
            const { gps, position } = updates.getSnapshot();
            const {
                Latitude = position?.latitude || 0,
                Longitude = position?.longitude || 0
            } = gps || {};
            marker.position = { lat: +Latitude, lng: +Longitude };
        };
        applyPosition();
        return updates.subscribe(applyPosition);
    }, [marker, updates]);

    useEffect(() => {
        const el = marker?.content as HTMLElement;
        if (!el) return;

        const handleMouseEnter = () => setVisible(true);
        const handleMouseMove = (event: MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            const { zonesLock, location } = get();
            if (!zonesLock && location !== undefined) {
                set({ location: undefined });
            }
        };
        const handleMouseLeave = () => setVisible(false);
        const handleContextMenu = (event: MouseEvent) => onContextmenu?.(event);

        el.addEventListener('mouseenter', handleMouseEnter);
        el.addEventListener('mousemove', handleMouseMove);
        el.addEventListener('mouseleave', handleMouseLeave);
        if (onContextmenu)
            el.addEventListener('contextmenu', handleContextMenu);

        return () => {
            el.removeEventListener('mouseenter', handleMouseEnter);
            el.removeEventListener('mousemove', handleMouseMove);
            el.removeEventListener('mouseleave', handleMouseLeave);
            if (onContextmenu)
                el.removeEventListener('contextmenu', handleContextMenu);
        };
    }, [marker, onContextmenu, get, set]);

    useEffect(() => {
        if (!popup) return;
        const element = marker?.content as HTMLElement | undefined;
        popup.update(popupKey, show && element && dialog && (visible || showPopup)
            ? { element, render: dialog, hovered: visible }
            : undefined);
    }, [popup, popupKey, marker, show, dialog, visible, showPopup]);

    useEffect(() => () => popup?.update(popupKey), [popup, popupKey]);

    useEffect(() => {
        if (!show) setVisible(false);
    }, [show]);

    const isDialog = show && (visible || showPopup);

    return <AdvancedMarker
        {...markerProps}
        ref={markerRef}
        zIndex={isDialog ? 10 : markerProperty[markerType]?.zIndex || 0}
    >
        {!popup && isDialog && dialog && <Dialog visible>{dialog()}</Dialog>}
        {children}
    </AdvancedMarker>;
}

const SiteMarker: React.FC<IPropsMarker> = ({ data, show = true, onClick }) => {
    const { name, diseases = {}, units, is_empty } = data;
    const { latitude = 0, longitude = 0 } = data.position || {};
    const [{ disease }] = diseases?.cases || [{}];
    const { color: background = is_empty ? '#9CA4AB' : '#1B6399' } = diseaseType[disease] || {};

    const dialog = useCallback(() => <div className={styles['container-info']}>
        <div className={[styles.header, styles['location-name']].join(' ')}>
            <div className={styles['site-icon']} style={{ background }} />
            <span>{name}</span>
        </div>
        {units && <div className={styles.contact}>
            <div className={styles.unit}>
                <div className={styles.icon} />
                <span>{units.length + ` ${units.length > 1 ? 'Units' : 'Unit'}`} </span>
            </div>
        </div>}
    </div>, [units, name, background]);

    return <Marker
        show={show}
        markerType={MAP_ELEMENT.SITE}
        dialog={dialog}
        markerProps={{
            onClick,
            title: name,
            position: { lat: +latitude, lng: +longitude }
        }}
    >
        <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 16,
            height: 16,
            background,
            border: '2px solid #FFF',
            borderRadius: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
        }}>
            <Name>{name}</Name>
        </div>
    </Marker>;
};

const FactoryMarker: React.FC<IPropsMarker> = ({ data, show = true, onClick }) => {
    const { phone, work_time, capacity_discharge, name } = data;
    const { latitude = 0, longitude = 0 } = data.position || {};

    const dialog = useCallback(() => {
        const { hour, minute } = getHourMinute(work_time);

        return <div className={styles['container-info']}>
            <div className={[styles.header, styles['location-name']].join(' ')}>
                <div className={styles['factory-icon']} />
                <span>{name}</span>
            </div>
            {phone && <div className={styles['contact']}>
                <div className={styles.phone}>
                    <div className={styles.icon} />
                    <span>{phone}</span>
                </div>
            </div>}
            {(work_time || capacity_discharge) && <div className={styles.detail}>
                {work_time && <div className={styles.info}>
                    <span>Opening time: <b>{checkZero(hour)}:{checkZero(minute)}</b></span>
                </div>}
                {capacity_discharge && <div className={styles.info}>
                    <span>Capacity/hours: <b>{capacity_discharge} tons</b></span>
                </div>}
            </div>}
        </div>
    }, [name, work_time, phone, capacity_discharge]);

    return <Marker
        show={show}
        markerType={MAP_ELEMENT.FACTORY}
        dialog={dialog}
        markerProps={{
            onClick,
            title: name,
            position: { lat: +latitude, lng: +longitude }
        }}
    >
        <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 15,
            height: 15,
            background: "#58BD28",
            border: '2px solid #FFF',
            transform: 'translate(-50%, -50%)'
        }}>
            <Name>{name}</Name>
        </div>
    </Marker>
}

const VesselIcon: React.FC<Pick<IPropsMarker, 'data'> & { updates?: VesselUpdates }> = ({ data, updates }) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const [zoom] = useWorldMap(store => store.zoom);
    const map = useMap();
    const { id, type: vesselType } = data;
    const { width = 10, length = 20, Heading } = data.gps || {};

    const icon = useMemo(() => {
        const metersPerPx = getMetersPerPx(map?.getCenter()?.lat() || 0, zoom);
        return createIconVessel({ width, length, metersPerPx, vesselType });
    }, [zoom, map, width, length, vesselType]);

    useEffect(() => {
        if (!updates) return;
        const applyHeading = () => {
            if (svgRef.current) {
                svgRef.current.style.transform = `rotate(${updates.getSnapshot().gps?.Heading}deg)`;
            }
        };
        applyHeading();
        return updates.subscribe(applyHeading);
    }, [updates]);

    return (
        <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: icon.width,
            height: icon.height,
            transform: `translate(-50%, -50%)`
        }}>
            <svg ref={svgRef} viewBox={`-${icon.x} -${icon.y} ${icon.x * 2} ${icon.y * 2}`}
                style={{ transform: `rotate(${updates ? updates.getSnapshot().gps?.Heading : Heading}deg)` }}>
                <defs>
                    <path id={id} d={icon.path} />
                    <clipPath id={`clip_${id}`}>
                        <use href={`#${id}`} />
                    </clipPath>
                </defs>
                <g>
                    <use
                        href={`#${id}`}
                        stroke={icon.strokeColor}
                        strokeWidth={icon.strokeWeight}
                        fill={icon.fillColor}
                        clipPath={`url(#clip_${id})`}
                    />
                </g>
            </svg>
        </div>
    );
};

const VesselPopup = ({ data }: Pick<IPropsMarker, 'data'>) => {
    const { name, capacity, gps, operation } = data;
    let statusTxt = 'Idle'
    if (operation) {
        const { current_process, activity_log } = operation
        statusTxt = getStatusOfOp({
            operation: operation.operation,
            current_process,
            activity_log,
        });
    }
    if (!gps)
        return null;
    const { Speed, Heading, Latitude, Longitude, time } = gps;

    return <div className={styles['vessel-info']}>
        <div className={styles.header}><b>{name}</b> - {statusTxt}</div>
        <div className={styles['speed-cargo']}>
            <div>
                <p>Speed / Heading</p>
                <b>{Math.round(Speed * 100) / 100} knots / {Math.round(Heading)}&#xb0;</b>
            </div>
            <div>
                <p>Cargo</p>
                <b>0 / {Math.round(capacity / 10000) / 100} t</b>
            </div>
        </div>
        <div className={styles['line-info']}>
            <p>Position:
                <b className="ml-1" title="Latitude">{(+Latitude).toFixed(6)}</b>,
                <b className="ml-1" title="Longitude">{(+Longitude).toFixed(6)}</b>
            </p>
        </div>
        <div className={styles['line-comment']}>
            <DurationReel time={time} />
        </div>
    </div>;
};

const LiveVesselPopup = ({ updates }: { updates: VesselUpdates }) => {
    const data = useSyncExternalStore(updates.subscribe, updates.getSnapshot);
    return <VesselPopup data={data} />;
};

interface IVesselMarkerProps extends IPropsMarker {
    updates?: VesselUpdates;
    onSelect?(data: IPropsMarker['data']): void;
}

const VesselMarker = React.memo(({ data, show = true, showPopup, onClick, updates, onSelect }: IVesselMarkerProps) => {
    const { name } = data;
    const { gps, position } = updates ? updates.getSnapshot() : data;
    const {
        Latitude = position?.latitude || 0,
        Longitude = position?.longitude || 0
    } = gps || {};

    // Keep the declarative position stable while live updates own marker.position.
    const initialPosition = useRef({ lat: +Latitude, lng: +Longitude });

    const dialog = useCallback(() => updates
        ? <LiveVesselPopup updates={updates} />
        : <VesselPopup data={data} />, [updates, data]);
    const handleClick = useCallback((event: google.maps.MapMouseEvent) => {
        onClick?.(event);
        onSelect?.(updates ? updates.getSnapshot() : data);
    }, [onClick, onSelect, updates, data]);

    return <Marker
        updates={updates}
        show={show}
        showPopup={showPopup}
        markerType={MAP_ELEMENT.VESSEL}
        dialog={dialog}
        markerProps={{
            onClick: handleClick,
            title: name,
            position: updates ? initialPosition.current : { lat: +Latitude, lng: +Longitude }
        }}
    >
        <VesselIcon data={data} updates={updates} />
    </Marker>
});

const PoiMarker: React.FC<IPropsMarker> = ({ data, show = true, onClick }) => {
    const { id, name } = data;
    const { latitude = 0, longitude = 0 } = data.position || {};

    const dialog = useCallback(() => <div className={styles['container-info']}>
        <div className={[styles.header, styles['location-name']].join(' ')}>
            <svg height="22" width="20">
                <polygon points="10 1,19 5.5,19 16.5, 10 21,1 16.5,1 5.5"
                    stroke="#F6F5F4"
                    fill="#F69C6F" strokeWidth="2" />
            </svg>
            <span>{name}</span>
        </div>
    </div>, [name]);

    return <Marker
        show={show}
        markerType={MAP_ELEMENT.POI}
        dialog={dialog}
        markerProps={{
            onClick,
            title: name,
            position: { lat: +latitude, lng: +longitude }
        }}
    >
        <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 15.2,
            height: 15.2,
            transform: 'translate(-50%, -50%)'
        }}>
            <svg viewBox="-6.3 -7 12.6 14">
                <defs>
                    <path id={id} d="M 0 -7 L 6.3 -3.5 L 6.3 3.5, 0 7 L -6.3 3.5 L -6.3 -3.5 Z" />
                    <clipPath id={`clip_${id}`}>
                        <use xlinkHref={`#${id}`} />
                    </clipPath>
                </defs>
                <g>
                    <use
                        xlinkHref={`#${id}`}
                        stroke="#FFF"
                        fill="#F69C6F"
                        strokeWidth="3.6"
                        clipPath={`url(#clip_${id})`}
                    />
                </g>
            </svg>
            <Name>{name}</Name>
        </div>
    </Marker>
};

const PointMarker: React.FC<IPropsMarker> = ({ data, show = true, onClick, onContextmenu, onUpdate }) => {
    const { name, lat = 0, lng = 0, color = colors[0] } = data;
    const fontColor = percentBrightness(color) < 85 ? '#FFF' : '#2D3939';
    const [dragging, setDragging] = useState(false);
    const size = onUpdate ? 18 : 15;

    const event = useMemo(() => {
        if (onUpdate) {
            return {
                draggable: true,
                onDragStart: () => setDragging(true),
                onDragEnd: (e: google.maps.MapMouseEvent) => {
                    setDragging(false)
                    onUpdate(e)
                }
            }
        } else {
            return {}
        }
    }, [onUpdate]);

    return <Marker
        show={show}
        markerType={MAP_ELEMENT.POINT}
        markerProps={{
            title: name,
            position: { lat, lng },
            onClick,
            clickable: !!onClick,
            ...event
        }}
        onContextmenu={onContextmenu}
    >
        <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: size + 'px',
            height: size + 'px',
            borderRadius: size + 'px',
            lineHeight: size + 'px',
            textAlign: 'center',
            color: fontColor,
            background: color,
            transform: ['translate(-50%, -50%)', dragging ? 'scale(1.05)' : ''].join(' ')
        }}>
            {name}
        </div>
    </Marker>
};

const markerByName: { [id: string]: React.FC<IPropsMarker> } = {
    [MAP_ELEMENT.SITE]: SiteMarker,
    [MAP_ELEMENT.FACTORY]: FactoryMarker,
    [MAP_ELEMENT.VESSEL]: VesselMarker,
    [MAP_ELEMENT.POI]: PoiMarker,
    [MAP_ELEMENT.POINT]: PointMarker,
}

export { FactoryMarker, markerByName, PoiMarker, PointMarker, SiteMarker, VesselMarker };
